import React, { useState, useEffect, useCallback, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner,
  faExclamationCircle,
  faRedo,
  faSyncAlt,
  faSearch,
  faTimes,
  faSortAmountDown,
  faSortAmountUp,
  faFilter,
  faCheck,
  faReceipt,
  faMapMarkerAlt,
  faCreditCard,
  faTag,
  faArrowRight,
  faChevronLeft,
  faChevronRight,
  faCalendarAlt,
} from "@fortawesome/free-solid-svg-icons";
import { faClock } from "@fortawesome/free-regular-svg-icons";
import apiService from "../../ApiService";
import { formatDateOnly } from "../../utils/DateUtils";
import "./BillList.css";

// Build list of month keys (YYYY-MM) for the last 10 years up to current month (newest first)
const getMonthOptions = () => {
  const options = [];
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth(), 1);
  const start = new Date(now.getFullYear() - 10, 0, 1);
  const d = new Date(end);
  while (d >= start) {
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    options.push({
      key,
      label: d.toLocaleDateString("en-US", { month: "short", year: "numeric" }),
    });
    d.setMonth(d.getMonth() - 1);
  }
  return options;
};

const MONTH_OPTIONS = getMonthOptions();

const getCurrentMonthKey = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}`;
};

const BillList = ({ onSelectBill, refreshTrigger }) => {
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalBills, setTotalBills] = useState(0);
  const [perPage] = useState(10);
  const [showFilters, setShowFilters] = useState(false);

  // Month filter: default current month only; user can select multiple
  const [selectedMonths, setSelectedMonths] = useState(() => [getCurrentMonthKey()]);

  // Search & filter state
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [merchantName, setMerchantName] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [monthDropdownOpen, setMonthDropdownOpen] = useState(false);

  const searchTimeout = useRef(null);
  const monthDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (monthDropdownRef.current && !monthDropdownRef.current.contains(e.target)) {
        setMonthDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchBills = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const userData = JSON.parse(localStorage.getItem("userData") || "{}");
      const params = {
        page: currentPage,
        per_page: perPage,
        user_id: userData.UserID || null,
        sort_by: sortBy,
        sort_order: sortOrder,
      };
      if (search.trim()) params.search = search.trim();
      if (category) params.category = category;
      if (merchantName.trim()) params.merchant_name = merchantName.trim();
      if (paymentMethod) params.payment_method = paymentMethod;
      // Month filter: when any month selected, use its range; otherwise use filter panel dates
      if (selectedMonths && selectedMonths.length > 0) {
        const sorted = [...selectedMonths].sort();
        const [minY, minM] = sorted[0].split("-").map(Number);
        params.date_from = `${minY}-${String(minM).padStart(2, "0")}-01`;
        const maxKey = sorted[sorted.length - 1];
        const [maxY, maxM] = maxKey.split("-").map(Number);
        const lastDay = new Date(maxY, maxM, 0);
        params.date_to = `${lastDay.getFullYear()}-${String(lastDay.getMonth() + 1).padStart(2, "0")}-${String(lastDay.getDate()).padStart(2, "0")}`;
      } else {
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
      }
      if (minAmount) params.min_amount = minAmount;
      if (maxAmount) params.max_amount = maxAmount;

      const response = await apiService.getBills(params);

      setBills(response.bills || []);
      setTotalPages(response.pagination?.total_pages || 1);
      setTotalBills(response.pagination?.total || 0);
    } catch (err) {
      setError(err.message || "Failed to fetch bills");
    } finally {
      setLoading(false);
    }
  }, [
    currentPage,
    perPage,
    search,
    category,
    merchantName,
    paymentMethod,
    selectedMonths,
    dateFrom,
    dateTo,
    minAmount,
    maxAmount,
    sortBy,
    sortOrder,
  ]);

  useEffect(() => {
    fetchBills();
  }, [fetchBills, refreshTrigger]);

  // Debounced search
  const handleSearchChange = (value) => {
    setSearch(value);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setCurrentPage(1);
    }, 400);
  };

  const handleFilterApply = () => {
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSearch("");
    setCategory("");
    setMerchantName("");
    setPaymentMethod("");
    setDateFrom("");
    setDateTo("");
    setMinAmount("");
    setMaxAmount("");
    setSortBy("created_at");
    setSortOrder("DESC");
    setSelectedMonths([getCurrentMonthKey()]);
    setCurrentPage(1);
  };

  const toggleMonth = (key) => {
    setSelectedMonths((prev) => {
      if (prev.includes(key)) {
        return prev.filter((m) => m !== key);
      }
      return [...prev, key].sort();
    });
    setCurrentPage(1);
  };

  const selectAllMonths = () => {
    setSelectedMonths([]);
    setCurrentPage(1);
    setMonthDropdownOpen(false);
  };

  const selectCurrentMonthOnly = () => {
    setSelectedMonths([getCurrentMonthKey()]);
    setCurrentPage(1);
    setMonthDropdownOpen(false);
  };

  const getMonthFilterLabel = () => {
    if (selectedMonths.length === 0) return "All months";
    if (selectedMonths.length === 1) {
      const opt = MONTH_OPTIONS.find((o) => o.key === selectedMonths[0]);
      return opt ? opt.label : selectedMonths[0];
    }
    return `${selectedMonths.length} months`;
  };

  const activeFilterCount = [
    category,
    merchantName,
    paymentMethod,
    dateFrom,
    dateTo,
    minAmount,
    maxAmount,
  ].filter(Boolean).length;

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const formatDate = (dateString) => formatDateOnly(dateString);

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return "$0.00";
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="bill-list-loading">
        <FontAwesomeIcon icon={faSpinner} spin />
        <p>Loading bills...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bill-list-error">
        <FontAwesomeIcon icon={faExclamationCircle} />
        <p>{error}</p>
        <button onClick={fetchBills} className="retry-btn">
          <FontAwesomeIcon icon={faRedo} /> Retry
        </button>
      </div>
    );
  }

  return (
    <div className="bill-list-container">
      <div className="bill-list-header">
        <h2>
          Bills{" "}
          {totalBills > 0 && <span className="bill-count">({totalBills})</span>}
        </h2>
        <div className="header-right">
          <button onClick={fetchBills} className="refresh-btn">
            <FontAwesomeIcon icon={faSyncAlt} /> Refresh
          </button>
        </div>
      </div>

      {/* Month filter dropdown */}
      <div className="bills-month-filter" ref={monthDropdownRef}>
        <span className="bills-month-filter-label">
          <FontAwesomeIcon icon={faCalendarAlt} /> Month:
        </span>
        <div className="bills-month-dropdown-wrap">
          <button
            type="button"
            className="bills-month-dropdown-trigger"
            onClick={() => setMonthDropdownOpen((o) => !o)}
            aria-expanded={monthDropdownOpen}
            aria-haspopup="listbox"
          >
            <span>{getMonthFilterLabel()}</span>
            <span className="bills-month-dropdown-arrow">▼</span>
          </button>
          {monthDropdownOpen && (
            <div className="bills-month-dropdown-panel" role="listbox">
              <div className="bills-month-dropdown-actions">
                <button type="button" onClick={selectAllMonths}>
                  All months
                </button>
                <button type="button" onClick={selectCurrentMonthOnly}>
                  Current month only
                </button>
              </div>
              <div className="bills-month-dropdown-list">
                {MONTH_OPTIONS.map(({ key, label }) => (
                  <label key={key} className="bills-month-dropdown-option">
                    <input
                      type="checkbox"
                      checked={selectedMonths.includes(key)}
                      onChange={() => {
                        toggleMonth(key);
                      }}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Search & Sort Bar */}
      <div className="bills-search-bar">
        <div className="search-input-wrapper">
          <FontAwesomeIcon icon={faSearch} />
          <input
            type="text"
            placeholder="Search bills by merchant, category, payment..."
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
          {search && (
            <button
              className="search-clear"
              onClick={() => {
                setSearch("");
                setCurrentPage(1);
              }}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}
        </div>
        <div className="sort-controls">
          <select
            value={sortBy}
            onChange={(e) => {
              setSortBy(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="created_at">Date Added</option>
            <option value="bill_date">Bill Date</option>
            <option value="total_amount">Amount</option>
            <option value="merchant_name">Merchant</option>
          </select>
          <button
            className="sort-order-btn"
            onClick={() => {
              setSortOrder((p) => (p === "DESC" ? "ASC" : "DESC"));
              setCurrentPage(1);
            }}
            title={sortOrder === "DESC" ? "Descending" : "Ascending"}
          >
            <FontAwesomeIcon
              icon={sortOrder === "DESC" ? faSortAmountDown : faSortAmountUp}
            />
          </button>
          <button
            className={`filter-toggle-btn ${showFilters ? "active" : ""}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FontAwesomeIcon icon={faFilter} />
            {activeFilterCount > 0 && (
              <span className="filter-badge">{activeFilterCount}</span>
            )}
          </button>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="bills-filter-panel">
          <div className="filter-row">
            <div className="filter-field">
              <label>Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="">All Categories</option>
                <option value="Grocery">Grocery</option>
                <option value="Restaurant">Restaurant</option>
                <option value="Gas">Gas</option>
                <option value="Shopping">Shopping</option>
                <option value="Utilities">Utilities</option>
                <option value="Healthcare">Healthcare</option>
                <option value="Entertainment">Entertainment</option>
                <option value="Travel">Travel</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="filter-field">
              <label>Merchant</label>
              <input
                type="text"
                placeholder="Store name..."
                value={merchantName}
                onChange={(e) => setMerchantName(e.target.value)}
              />
            </div>
            <div className="filter-field">
              <label>Payment</label>
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
              >
                <option value="">All Methods</option>
                <option value="Credit Card">Credit Card</option>
                <option value="Debit Card">Debit Card</option>
                <option value="Cash">Cash</option>
                <option value="UPI">UPI</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>
          <div className="filter-row">
            <div className="filter-field">
              <label>Date From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <div className="filter-field">
              <label>Date To</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
            <div className="filter-field">
              <label>Min Amount</label>
              <input
                type="number"
                step="0.01"
                placeholder="$0.00"
                value={minAmount}
                onChange={(e) => setMinAmount(e.target.value)}
              />
            </div>
            <div className="filter-field">
              <label>Max Amount</label>
              <input
                type="number"
                step="0.01"
                placeholder="$999.99"
                value={maxAmount}
                onChange={(e) => setMaxAmount(e.target.value)}
              />
            </div>
          </div>
          <div className="filter-actions">
            <button className="btn-apply-filter" onClick={handleFilterApply}>
              <FontAwesomeIcon icon={faCheck} /> Apply
            </button>
            <button className="btn-clear-filter" onClick={handleClearFilters}>
              <FontAwesomeIcon icon={faTimes} /> Clear All
            </button>
          </div>
        </div>
      )}

      {bills.length === 0 ? (
        <div className="no-bills">
          <FontAwesomeIcon icon={faReceipt} />
          <p>No bills found</p>
          <p className="no-bills-subtitle">
            Upload your first bill to get started
          </p>
        </div>
      ) : (
        <>
          <div className="bills-grid">
            {bills.map((bill) => (
              <div
                key={bill.bill_id}
                className="bill-card"
                onClick={() => onSelectBill && onSelectBill(bill.bill_id)}
              >
                <div className="bill-card-header">
                  <h3>{bill.merchant_name || "Unknown Merchant"}</h3>
                  <span className="bill-date">
                    {formatDate(bill.bill_date)}
                  </span>
                </div>

                <div className="bill-card-body">
                  {bill.merchant_address && (
                    <p className="merchant-address">
                      <FontAwesomeIcon icon={faMapMarkerAlt} />
                      {bill.merchant_address}
                    </p>
                  )}

                  <div className="bill-amount">
                    <span className="amount-label">Total:</span>
                    <span className="amount-value">
                      {formatCurrency(bill.total_amount)}
                    </span>
                  </div>

                  {bill.payment_method && (
                    <div className="payment-method">
                      <FontAwesomeIcon icon={faCreditCard} />
                      {bill.payment_method}
                    </div>
                  )}

                  {bill.category && (
                    <div className="bill-category">
                      <FontAwesomeIcon icon={faTag} />
                      {bill.category}
                    </div>
                  )}
                </div>

                <div className="bill-card-footer">
                  <span className="bill-time">
                    <FontAwesomeIcon icon={faClock} />
                    {bill.bill_time || "N/A"}
                  </span>
                  <button
                    className="view-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectBill && onSelectBill(bill.bill_id);
                    }}
                  >
                    View Details <FontAwesomeIcon icon={faArrowRight} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="page-btn"
              >
                <FontAwesomeIcon icon={faChevronLeft} /> Previous
              </button>

              <div className="page-numbers">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`page-number ${
                        page === currentPage ? "active" : ""
                      }`}
                    >
                      {page}
                    </button>
                  ),
                )}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="page-btn"
              >
                Next <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BillList;
